import { type ReactElement, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { useLanguage } from '../../../app/i18n';
import { ONBOARDING_ROUTES } from '../routes';

import { TradingAccountScreen } from './TradingAccountScreen';
import { toTradingAccountView } from './trading-account-view';
import { useTradingAccount } from './use-trading-account';
import { useTradingAccountWrites } from './use-trading-account-writes';

/**
 * Route element of E4 (specs/S6-trading-account/spec.md): reads the account, polls while it is
 * verified, sends the connection and the consent, and leads to E5 after the consent or from an
 * account that is already connected (R17, R30).
 */
export function TradingAccountRoute(): ReactElement {
  const navigate = useNavigate();
  const language = useLanguage();
  const account = useTradingAccount();
  const toFirstSettings = useCallback(() => {
    void navigate(ONBOARDING_ROUTES.firstSettings);
  }, [navigate]);
  // A newer read is the server's truth: the hook drops the 202 answer kept for the previous one.
  const writes = useTradingAccountWrites({
    base: account.data,
    watch: account.watch,
    reload: account.refetch,
    onConsented: toFirstSettings,
  });
  const view = useMemo(() => toTradingAccountView(account, writes), [account, writes]);

  return (
    <TradingAccountScreen
      view={view}
      kept={writes.kept}
      consentBusy={writes.consenting}
      consentFailure={writes.consentFailure}
      consentEpoch={writes.consentEpoch}
      onSubmit={writes.connect}
      onAuthorize={() => {
        writes.consent(language);
      }}
      onRetry={account.refetch}
      onContinue={toFirstSettings}
    />
  );
}
